/**
 * User-facing labels for session / lifecycle / payment statuses.
 * Never expose OCPP or backend enum names on customer-facing screens.
 */

const SESSION_STATUS_COPY = {
  pending:    { label: 'Pending',            tone: 'neutral' },
  authorized: { label: 'Ready to charge',    tone: 'info' },
  starting:   { label: 'Starting',           tone: 'info' },
  charging:   { label: 'Charging',           tone: 'success' },
  stopping:   { label: 'Stopping',           tone: 'info' },
  completed:  { label: 'Completed',          tone: 'success' },
  cancelled:  { label: 'Cancelled',          tone: 'neutral' },
  voided:     { label: 'Voided',             tone: 'neutral' },
  failed:     { label: 'Failed',             tone: 'danger' },
};

const OCPP_STATUS_COPY = {
  Available:      { label: 'Ready to charge', tone: 'success' },
  Preparing:      { label: 'Getting ready',   tone: 'info' },
  Charging:       { label: 'Charging',        tone: 'success' },
  SuspendedEV:    { label: 'Paused by vehicle', tone: 'info' },
  SuspendedEVSE:  { label: 'Paused by charger', tone: 'info' },
  Finishing:      { label: 'Finishing up',    tone: 'info' },
  Reserved:       { label: 'Reserved',        tone: 'warning' },
  Unavailable:    { label: 'Unavailable',     tone: 'danger' },
  Faulted:        { label: 'Out of service',  tone: 'danger' },
  Occupied:       { label: 'In use',          tone: 'warning' },
  Busy:           { label: 'Busy',            tone: 'warning' },
  Disabled:       { label: 'Disabled',        tone: 'danger' },
};

const PAYMENT_STATUS_COPY = {
  authorizing: { label: 'Authorizing…', tone: 'info' },
  authorized:  { label: 'Authorized',   tone: 'success' },
  capturing:   { label: 'Finalising…',  tone: 'info' },
  paid:        { label: 'Paid',         tone: 'success' },
  voided:      { label: 'Cancelled',    tone: 'neutral' },
  voiding:     { label: 'Cancelling…',  tone: 'neutral' },
  error:       { label: 'Payment issue', tone: 'danger' },
};

export function sessionStatusCopy(status) {
  if (!status) return { label: 'Unknown', tone: 'neutral' };
  return SESSION_STATUS_COPY[status] || { label: 'Unknown', tone: 'neutral' };
}

export function ocppStatusCopy(status) {
  if (!status) return { label: 'Checking…', tone: 'neutral' };
  return OCPP_STATUS_COPY[status] || { label: 'Unknown', tone: 'neutral' };
}

export function paymentStatusCopy(status) {
  if (!status) return { label: '—', tone: 'neutral' };
  return PAYMENT_STATUS_COPY[status] || { label: '—', tone: 'neutral' };
}

/**
 * Recorded payment state for a receipt WITHOUT `billing.display`.
 *
 * A session's type, amounts and billing status come from the backend's
 * `billing.display` (see `utils/billingDisplay.js`). This helper is only
 * the fallback for a response that carries no `billing`: it names what
 * happened to the driver's payment and nothing more. It never names a
 * session type, and a zero amount is never turned into one.
 *
 *   - Paid                  — card capture succeeded and > 0
 *   - Reimbursement covered — CREDIT billing mode OR a redeem code
 *                             covers the whole session
 *   - Payment pending       — authorising / authorised / capturing
 *   - Payment failed        — payment_status = 'error'
 *   - Cancelled             — voided / voiding
 *   - No payment taken      — a `paid` record with nothing captured
 *   - —                     — no signal at all (defensive)
 *
 * All amounts are integer cents.
 */
export function receiptPaymentCopy({
  billingMode,
  paymentStatus,
  capturedCents,
  creditAppliedCents,
  totalCents,
} = {}) {
  const ps = paymentStatus || null;
  const captured = Number.isFinite(capturedCents) ? capturedCents : null;
  const credit = Number.isFinite(creditAppliedCents) ? creditAppliedCents : 0;
  const total = Number.isFinite(totalCents) ? totalCents : null;

  if (ps === 'error') return { label: 'Payment failed', tone: 'danger' };
  if (ps === 'voided' || ps === 'voiding') {
    return { label: 'Cancelled', tone: 'neutral' };
  }

  // Credit paths — either the whole session is CREDIT (fully covered
  // by a reimbursement code), or a partial reimbursement fully covers
  // the session total.
  const fullyCoveredByCredit =
    billingMode === 'CREDIT' || (total != null && credit > 0 && credit >= total);
  if (fullyCoveredByCredit) return { label: 'Reimbursement covered', tone: 'success' };

  if (ps === 'paid') {
    // A `paid` record with nothing captured must not read as "Paid".
    if (captured === 0) return { label: 'No payment taken', tone: 'neutral' };
    return { label: 'Paid', tone: 'success' };
  }
  if (ps === 'authorizing' || ps === 'authorized' || ps === 'capturing') {
    return { label: 'Payment pending', tone: 'info' };
  }

  // No payment_status. A captured amount is the only evidence left;
  // without one, say nothing rather than guess.
  if (captured != null && captured > 0) return { label: 'Paid', tone: 'success' };
  return { label: '—', tone: 'neutral' };
}

/**
 * "Payment Status" on the charge summary of a CARD session (C-02).
 *
 * Only the server knows whether money was taken. The summary used to say
 * "Paid" whenever the bundle was built with the mock provider or the card was
 * the browser-only demo card — functional validation saw "Paid … charged 1.06"
 * while the server had captured nothing (`payment_status='bypassed'`).
 *
 * "Paid" now requires the server's own record: payment status paid/captured
 * AND a captured amount above zero.
 *
 * @param {{status?: string|null, payment_amount?: number|string|null}|null|undefined} serverPayment
 *   `session.payment` from GET /api/ev/sessions/:uid
 * @returns {'Paid'|'No payment taken'|'Not charged'|'Payment issue'|'Processing'}
 */
export function cardSummaryPaymentLabel(serverPayment) {
  const status = serverPayment?.status || null;
  const raw = serverPayment?.payment_amount;
  const amount = raw === null || raw === undefined || raw === '' ? null : Number(raw);
  if (status === 'paid' || status === 'captured') {
    if (amount !== null && Number.isFinite(amount) && amount > 0) return 'Paid';
    if (amount === 0) return 'No payment taken';
    return 'Processing';
  }
  if (status === 'voided' || status === 'waived') return 'No payment taken';
  if (status === 'failed' || status === 'void_failed') return 'Payment issue';
  // Finalized without any capture (no hold existed): never claim payment.
  if (status === 'bypassed') return 'Not charged';
  return 'Processing';
}

/** Whether the server recorded a card capture for this session. */
export function serverCapturedCard(serverPayment) {
  return cardSummaryPaymentLabel(serverPayment) === 'Paid';
}
